import { hasInjectionContext, inject, type InjectionKey, type Ref } from 'vue'

export interface SessionGuild {
  readonly id: string
  readonly name: string
}

export interface SessionUser {
  readonly userId: string
  readonly discordUserId: string
  readonly username: string
  readonly avatar: string | null
  readonly officerFor: ReadonlyArray<SessionGuild>
  readonly ownerFor: ReadonlyArray<SessionGuild>
  readonly memberFor: ReadonlyArray<SessionGuild>
}

export interface SessionPort {
  readonly user: Readonly<Ref<SessionUser | null>>
  readonly loading: Readonly<Ref<boolean>>
  isMemberOf(guildId: string | number): boolean
  isOfficerOf(guildId: string | number): boolean
  isOwnerOf(guildId: string | number): boolean
  login(): Promise<void>
  logout(): Promise<void>
}

export const SESSION_PORT: InjectionKey<SessionPort> = Symbol('SessionPort')

/**
 * Host-installed fallback for callers outside a Vue setup context.
 *
 * Mirrors the `GUILD_PORT` fallback pattern (`@/shared/ports/guild`):
 * setup-context callers get the injected instance; non-setup callers
 * (the feature install entry, nav-link builders, host test harnesses)
 * get the host-installed module singleton. See `_setGuildPortFallback`
 * for the rationale.
 */
let fallbackSessionPort: SessionPort | null = null

/** Host-only: register (or clear with `null`) the non-injection fallback. */
export function _setSessionPortFallback(p: SessionPort | null): void {
  fallbackSessionPort = p
}

/** Test-only: clear the registered fallback. */
export function _resetSessionPortFallback(): void {
  fallbackSessionPort = null
}

export const useSession = (): SessionPort => {
  // Only attempt `inject()` when we know we're inside a setup context.
  // Without this guard Vue emits `[Vue warn] inject() can only be used
  // inside setup() or functional components.` for every non-setup call,
  // even when there's a default value (the warn fires before the
  // default is consulted, so the default can't suppress it).
  if (hasInjectionContext()) {
    const fromInject = inject(SESSION_PORT, null)
    if (fromInject) return fromInject
  }
  if (fallbackSessionPort) return fallbackSessionPort
  throw new Error('SessionPort not provided')
}
